import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { useAuth } from '../../context/AuthContext';
import { getToken } from '../../services/tokenStore';

// pdfjs-dist v4 (upgraded from the CVE-2024-4367-affected 2.16.105 — arbitrary
// JS execution from a crafted PDF, security review SEC-003) ships an ESM-only
// worker. Deliberately NOT `new URL('pdfjs-dist/...', import.meta.url)`:
// react-scripts' webpack config runs Babel over any .mjs it bundles (to
// down-level this file's private class fields) with absoluteRuntime
// defaulting to true, which injects `import ... from "<build-machine's
// absolute path>/@babel/runtime/helpers/..."`. Those imports never get
// resolved because this file is emitted as a standalone asset, not bundled
// — so the worker 404s in the browser. scripts/copy-pdf-worker.js (via
// postinstall) copies it into public/ untouched by any loader instead.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const API_BASE_URL = `${(
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '')}/api`;

// pdfUrl is sometimes our own /api/documents/:id/pdf (now auth-gated —
// SEC-001) and sometimes a direct Azure Blob SAS URL (auth via the token in
// its query string, not a header). Only attach the app's Authorization
// header for the former: sending it cross-origin to Azure would add a
// preflight-triggering custom header that Azure's CORS config doesn't
// necessarily allow, which would break the SAS-URL case instead of fixing it.
const isOwnBackendUrl = (url) => typeof url === 'string' && url.startsWith(API_BASE_URL);

// After a redeploy, an already-open tab's bundle can still reference a
// hashed worker filename the new deploy no longer serves (404 -> pdf.js's
// _setupFakeWorker falls back to a same-thread dynamic import, which then
// fails with this message). A one-time reload picks up the current bundle;
// guarded by sessionStorage so a persistent failure doesn't reload-loop.
const isStaleAssetError = (err) =>
  /dynamically imported module|setting up fake worker/i.test(err?.message || '');

const PdfJsHighlightViewer = React.forwardRef(({ pdfId, pdfUrl, programmaticHighlights = [], readOnly = false }, ref) => {
  const [numPages, setNumPages] = useState(0);
  const [pdf, setPdf] = useState(null);
  const [highlights, setHighlights] = useState([]); // {page, left, top, width, height, comment}
  const [drawing, setDrawing] = useState(false);
  const [drawingPage, setDrawingPage] = useState(null);
  const [start, setStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [pendingRect, setPendingRect] = useState(null);
  const [activeHighlight, setActiveHighlight] = useState(null);
  const [commentBoxPageDims, setCommentBoxPageDims] = useState({}); // {width, height}
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // NEW: track current page
  const [replyInput, setReplyInput] = useState('');
  const [replyingTo, setReplyingTo] = useState(null); // highlight key

  const { user } = useAuth();

  const pageRefs = useRef({}); // {pageNum: ref}

  // Restore last viewed page from localStorage on mount or pdfId change
  useEffect(() => {
    if (!pdfId) return;
    const savedPage = localStorage.getItem(`pdf_last_page_${pdfId}`);
    if (savedPage) {
      setCurrentPage(Number(savedPage));
    } else {
      setCurrentPage(1);
    }
  }, [pdfId]);

  // Persist current page to localStorage when it changes
  useEffect(() => {
    if (!pdfId) return;
    localStorage.setItem(`pdf_last_page_${pdfId}`, currentPage);
  }, [currentPage, pdfId]);

  useEffect(() => {
    const loadPdf = async () => {
      // /api/documents/:id/pdf now requires auth (deny-by-default gate) — pdf.js's
      // own fetch for `url` doesn't carry the app's Authorization header unless
      // passed explicitly via httpHeaders.
      const token = getToken();
      const loadingTask = pdfjsLib.getDocument({
        url: pdfUrl,
        isEvalSupported: false,
        httpHeaders: token && isOwnBackendUrl(pdfUrl) ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const loadedPdf = await loadingTask.promise;
      setPdf(loadedPdf);
      setNumPages(loadedPdf.numPages);
      setLoadError(null);
    };
    if (pdfUrl) {
      loadPdf().catch((err) => {
        console.error('[PdfJsHighlightViewer] Failed to load PDF:', err);
        const reloadKey = 'pdf_stale_asset_reload_attempted';
        if (isStaleAssetError(err) && !sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
          return;
        }
        setLoadError('Failed to load PDF. Please try reloading the page.');
      });
    }
  }, [pdfUrl]);

  // Log when programmatic highlights change
  useEffect(() => {
    if (programmaticHighlights && programmaticHighlights.length > 0) {
      console.log('[PdfJsHighlightViewer] Programmatic highlights received:', programmaticHighlights);
    }
  }, [programmaticHighlights]);

  // Fetch highlights from backend on mount
  // Only load user-created highlights (not programmatic ones, which come from props)
  useEffect(() => {
    const fetchHighlights = async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/pdf-highlights?pdfId=${encodeURIComponent(pdfId)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (res.ok && res.status !== 405) {
          // Check if response is JSON before parsing
          const contentType = res.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            try {
              const data = await res.json();
              const allHighlights = data.highlights || [];
              // Only set user-created highlights (programmatic ones come from props)
              const userHighlights = allHighlights.filter(h => !h.isProgrammatic);
              setHighlights(userHighlights);
              setHasLoaded(true);
            } catch (jsonErr) {
              // Response is not valid JSON (likely HTML error page)
              setHasLoaded(true); // Still mark as loaded to allow saving
            }
          } else {
            // Response is not JSON (likely HTML error page)
            setHasLoaded(true); // Still mark as loaded to allow saving
          }
        } else {
          // API endpoint not found, 405, or other error - silently continue
          setHasLoaded(true); // Still mark as loaded to allow saving
        }
      } catch (err) {
        // Network error or other issue - silently continue
        console.warn('[PdfJsHighlightViewer] Error fetching highlights:', err.message);
        setHasLoaded(true); // Still mark as loaded to allow saving
      }
    };
    if (pdfId) fetchHighlights();
  }, [pdfId]);

  useEffect(() => {
    if (!pdfId || !hasLoaded) return;
    const saveHighlights = async () => {
      try {
        // Include programmatic highlights when saving (they come from props)
        // Merge user highlights with programmatic highlights for saving
        const userHighlights = (highlights || []).map(h => ({ ...h, isProgrammatic: false }));
        const progHighlights = (programmaticHighlights || []).map(h => ({ ...h, isProgrammatic: true }));
        const allHighlightsToSave = [...userHighlights, ...progHighlights];
        
        const token = getToken();
        const saveRes = await fetch(`${API_BASE_URL}/pdf-highlights`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ pdfId, highlights: allHighlightsToSave }),
        });
        
        if (!saveRes.ok || saveRes.status === 405) {
          // Silently handle save errors (including 405) - highlights will still work, just won't be persisted
          // API endpoint may not exist in production
        } else {
          const contentType = saveRes.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            // Silently handle - API may not be available
          }
        }
      } catch (saveErr) {
        // Silently handle save errors - highlights will still work, just won't be persisted
        console.warn('[PdfJsHighlightViewer] Error saving highlights (non-critical):', saveErr.message);
      }
    };
    saveHighlights();
  }, [highlights, pdfId, hasLoaded, programmaticHighlights]);

  // Mouse events for drawing
  const handleMouseDown = (e, pageNum) => {
    if (readOnly) return;
    if (showCommentBox) return;
    const rect = pageRefs.current[pageNum].getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setStart({ x, y });
    setDrawing(true);
    setDrawingPage(pageNum);
    setCurrentRect(null);
  };

  const handleMouseMove = (e, pageNum) => {
    if (readOnly) return;
    if (!drawing || !start || drawingPage !== pageNum) return;
    const rect = pageRefs.current[pageNum].getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const left = Math.min(start.x, x);
    const top = Math.min(start.y, y);
    const width = Math.abs(x - start.x);
    const height = Math.abs(y - start.y);
    setCurrentRect({ left, top, width, height });
  };

  const handleMouseUp = (e, pageNum, pageWidth, pageHeight) => {
    if (readOnly) return;
    if (drawing && currentRect && currentRect.width > 0.01 && currentRect.height > 0.01) {
      setPendingRect({ ...currentRect, page: pageNum });
      setShowCommentBox(true);
      setCommentInput('');
      setCommentBoxPageDims({ pageWidth, pageHeight });
    }
    setDrawing(false);
    setStart(null);
    setCurrentRect(null);
    setDrawingPage(null);
  };

  const handleAddComment = () => {
    if (readOnly) return;
    if (pendingRect && commentInput.trim() && user) {
      const newHighlight = {
        ...pendingRect,
        comment: {
          text: commentInput,
          author: {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
          },
          timestamp: new Date().toISOString(),
        },
        replies: []
      };
      setHighlights(prev => {
        const updated = [...prev, newHighlight];
        setActiveHighlight({ ...newHighlight, pageWidth: commentBoxPageDims.pageWidth, pageHeight: commentBoxPageDims.pageHeight });
        return updated;
      });
      setPendingRect(null);
      setShowCommentBox(false);
      setCommentInput('');
    }
  };

  // Add reply to a highlight
  const handleAddReply = (hl) => {
    if (readOnly) return;
    if (replyInput.trim() && user) {
      const newReply = {
        text: replyInput,
        author: {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
        },
        timestamp: new Date().toISOString(),
      };
      setHighlights(prev => prev.map(h =>
        highlightKey(h) === highlightKey(hl)
          ? { ...h, replies: [...(h.replies || []), newReply] }
          : h
      ));
      setActiveHighlight(ah => ah ? { ...ah, replies: [...(ah.replies || []), newReply] } : ah);
      setReplyInput('');
      setReplyingTo(null);
    }
  };

  // Merge programmatic highlights with user highlights
  const allHighlights = React.useMemo(() => {
    const userHighlights = (highlights || []).map(h => ({ ...h, isProgrammatic: false }));
    const progHighlights = (programmaticHighlights || []).map(h => ({ ...h, isProgrammatic: true }));
    const merged = [...userHighlights, ...progHighlights];
    console.log('[PdfJsHighlightViewer] Merged highlights:', {
      userCount: userHighlights.length,
      programmaticCount: progHighlights.length,
      total: merged.length,
      programmaticHighlights: progHighlights,
      currentPage: currentPage
    });
    return merged;
  }, [highlights, programmaticHighlights, currentPage]);

  // Expose navigation function via ref (for parent components)
  const navigateToPage = React.useCallback((pageNum) => {
    if (pageNum >= 1 && pageNum <= numPages) {
      setCurrentPage(pageNum);
      // Scroll to page
      setTimeout(() => {
        const pageElement = pageRefs.current[pageNum];
        if (pageElement) {
          pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [numPages]);

  // Expose functions via ref (for parent components)
  React.useImperativeHandle(ref, () => ({
    navigateToPage,
    setCurrentPage,
  }), [navigateToPage]);

  const highlightKey = (hl) => `${hl.page}-${hl.left}-${hl.top}-${hl.width}-${hl.height}`;
  const renderHighlights = (pageNum, pageWidth, pageHeight) => {
    const pageHighlights = allHighlights.filter(h => {
      const matches = h.page === pageNum;
      if (matches && h.isProgrammatic) {
        console.log(`[PdfJsHighlightViewer] Found programmatic highlight for page ${pageNum}:`, h);
      }
      return matches;
    });
    if (pageHighlights.length > 0) {
      console.log(`[PdfJsHighlightViewer] Rendering ${pageHighlights.length} highlights for page ${pageNum} (width: ${pageWidth}, height: ${pageHeight}):`, pageHighlights);
    }
    return pageHighlights.map((hl) => {
      const left = hl.left * pageWidth;
      const top = hl.top * pageHeight;
      const width = hl.width * pageWidth;
      const height = hl.height * pageHeight;
      console.log(`[PdfJsHighlightViewer] Rendering highlight at: left=${left}, top=${top}, width=${width}, height=${height}`, hl);
      return (
        <div
          key={highlightKey(hl)}
          style={{
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            background: hl.isProgrammatic ? 'rgba(255, 235, 59, 0.5)' : 'rgba(255, 230, 102, 0.4)',
            border: `2px solid ${hl.isProgrammatic ? '#ffc107' : '#ffe066'}`,
            cursor: 'pointer',
            zIndex: 10,
            pointerEvents: 'auto',
            boxShadow: hl.isProgrammatic ? '0 0 10px rgba(255, 193, 7, 0.7)' : '0 0 4px rgba(255, 224, 102, 0.4)',
            borderRadius: '3px',
            mixBlendMode: hl.isProgrammatic ? 'multiply' : 'normal',
          }}
          onClick={() => {
            console.log('Clicked highlight:', hl);
            setActiveHighlight({ ...hl, pageWidth, pageHeight });
          }}
          title={hl.isProgrammatic ? `Extracted from: ${hl.fieldPath || 'unknown'}` : 'User highlight'}
        />
      );
    });
  };

  // Render current drawing rectangle
  const renderCurrentRect = (pageNum, pageWidth, pageHeight) =>
    drawing && drawingPage === pageNum && currentRect ? (
      <div
        style={{
          position: 'absolute',
          left: currentRect.left * pageWidth,
          top: currentRect.top * pageHeight,
          width: currentRect.width * pageWidth,
          height: currentRect.height * pageHeight,
          background: 'rgba(102, 153, 255, 0.2)',
          border: '2px dashed #6699ff',
          zIndex: 3,
          pointerEvents: 'none',
        }}
      />
    ) : null;

  // Render comment popup
  const renderCommentPopup = () =>
    activeHighlight ? (
      <div
        style={{
          position: 'fixed',
          left: 500,
          top: 500,
          background: '#fffbe6',
          border: '1px solid #ffe066',
          borderRadius: 4,
          padding: 8,
          zIndex: 1000,
          minWidth: 180,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <b>Comment:</b> {typeof activeHighlight.comment === 'object' ? (
            <>
              <strong>{activeHighlight.comment.author?.firstName} {activeHighlight.comment.author?.lastName}:</strong> {activeHighlight.comment.text}
              <div style={{ fontSize: '0.8em', color: '#888' }}>{activeHighlight.comment.timestamp ? new Date(activeHighlight.comment.timestamp).toLocaleString() : ''}</div>
            </>
          ) : (
            activeHighlight.comment
          )}
        </div>
        {/* Replies */}
        {activeHighlight.replies && activeHighlight.replies.length > 0 && (
          <div style={{ marginBottom: 8, marginLeft: 16 }}>
            <b>Replies:</b>
            <ul style={{ paddingLeft: 16 }}>
              {activeHighlight.replies.map((reply, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>
                  {typeof reply === 'string' ? (
                    reply
                  ) : (
                    <>
                      <strong>{reply.author?.firstName} {reply.author?.lastName}:</strong> {reply.text}
                      <div style={{ fontSize: '0.8em', color: '#888' }}>{reply.timestamp ? new Date(reply.timestamp).toLocaleString() : ''}</div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Reply input */}
        {readOnly ? null : (replyingTo === highlightKey(activeHighlight) ? (
          <div style={{ marginBottom: 8 }}>
            <textarea
              rows={2}
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Add a reply..."
              value={replyInput}
              onChange={e => setReplyInput(e.target.value)}
            />
            <button onClick={() => handleAddReply(activeHighlight)} style={{ marginRight: 8 }}>Add Reply</button>
            <button onClick={() => { setReplyingTo(null); setReplyInput(''); }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => { setReplyingTo(highlightKey(activeHighlight)); setReplyInput(''); }} style={{ marginRight: 8 }}>Reply</button>
        ))}
        <button onClick={() => setActiveHighlight(null)}>Close</button>
      </div>
    ) : null;

  // Render comment input box
  const renderCommentBox = (pageNum, pageWidth, pageHeight) =>
    !readOnly && showCommentBox && pendingRect && pendingRect.page === pageNum ? (
      <div
        style={{
          position: 'absolute',
          left: pendingRect.left * pageWidth + pendingRect.width * pageWidth + 8,
          top: pendingRect.top * pageHeight,
          background: '#fafafa',
          border: '1px solid #aaa',
          borderRadius: 4,
          padding: 8,
          zIndex: 10,
          minWidth: 180,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <b>Add a comment:</b>
        </div>
        <textarea
          rows={2}
          style={{ width: '100%', marginBottom: 8 }}
          placeholder="Add a comment..."
          value={commentInput}
          onChange={e => setCommentInput(e.target.value)}
        />
        <div>
          <button onClick={handleAddComment} style={{ marginRight: 8 }}>Add</button>
          <button onClick={() => { setShowCommentBox(false); setPendingRect(null); }}>Cancel</button>
        </div>
      </div>
    ) : null;

  // Render all pages
  return (
    <div style={{ width: '100%', maxHeight: 900, overflow: 'auto', background: '#eee', padding: 16 }}>
      <div style={{ marginBottom: 8 }}>
        <b>PDF.js Highlight Mode:</b> {readOnly ? 'Read-only (comments are locked after approval).' : 'Click and drag on any page to highlight and comment.'}
      </div>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage <= 1}
        >
          Previous
        </button>
        <span>Page {currentPage} of {numPages}</span>
        <button
          onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
          disabled={currentPage >= numPages}
        >
          Next
        </button>
      </div>
      {loadError && (
        <div style={{ padding: 16, color: '#b00020' }}>
          {loadError}{' '}
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      )}
      {numPages > 0 && (
        <PDFPage
          key={currentPage}
          pdf={pdf}
          pageNum={currentPage}
          pageRefs={pageRefs}
          handleMouseDown={handleMouseDown}
          handleMouseMove={handleMouseMove}
          handleMouseUp={handleMouseUp}
          renderHighlights={renderHighlights}
          renderCurrentRect={renderCurrentRect}
          renderCommentBox={renderCommentBox}
        />
      )}
      {renderCommentPopup()}
    </div>
  );
});

// Helper component to render a single PDF page and its overlay
const PDFPage = ({
  pdf,
  pageNum,
  pageRefs,
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  renderHighlights,
  renderCurrentRect,
  renderCommentBox,
}) => {
  const [viewport, setViewport] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const canvasRef = useRef();
  const renderTaskRef = useRef(null); // NEW: persist renderTask

  useEffect(() => {
    // Cancel any previous render task
    if (renderTaskRef.current && renderTaskRef.current.cancel) {
      renderTaskRef.current.cancel();
    }

    const renderPage = async () => {
      if (!pdf) return;
      const page = await pdf.getPage(pageNum);
      const scale = 1.5;
      const vp = page.getViewport({ scale });
      setViewport(vp);
      setPageWidth(vp.width);
      setPageHeight(vp.height);

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = vp.width;
      canvas.height = vp.height;

      // Start rendering and keep a reference to the renderTask
      renderTaskRef.current = page.render({ canvasContext: context, viewport: vp });
      try {
        await renderTaskRef.current.promise;
      } catch (e) {
        // Ignore render cancellation errors
      }
    };
    renderPage();

    // Cleanup: cancel the render task if the component unmounts or rerenders
    return () => {
      if (renderTaskRef.current && renderTaskRef.current.cancel) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdf, pageNum]);

  return (
    <div
      ref={el => (pageRefs.current[pageNum] = el)}
      style={{
        position: 'relative',
        margin: '24px auto',
        background: '#fff',
        width: pageWidth,
        height: pageHeight,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
      onMouseDown={e => handleMouseDown(e, pageNum)}
      onMouseMove={e => handleMouseMove(e, pageNum)}
      onMouseUp={e => handleMouseUp(e, pageNum, pageWidth, pageHeight)}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
      {/* Overlay for highlights and comment box */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: pageWidth, height: pageHeight }}>
        {/* Highlights are clickable */}
        <div style={{ pointerEvents: 'auto' }}>
          {renderHighlights(pageNum, pageWidth, pageHeight)}
        </div>
        {/* Drawing rectangle and comment box */}
        <div style={{ pointerEvents: 'none' }}>
          {renderCurrentRect(pageNum, pageWidth, pageHeight)}
          <div style={{ pointerEvents: 'auto' }}>
            {renderCommentBox(pageNum, pageWidth, pageHeight)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfJsHighlightViewer; 